import type React from "react";

import { RoomType } from "@eisox/api/core-api";
import { Alert, Button, CanAccess, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon } from "@eisox/icons";
import { getDirtyValues, useMediaQuery } from "@eisox/tools";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { roomApi } from "~/api";
import { DEFAULT_NIGHT_TEMPERATURE } from "~/constants/building.constant";
import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";
import { clampTemperature, getRoomTemperatureBounds } from "~/helpers/temperature.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { useRoomContext } from "~/providers/room.provider";

const NAME = "TemperatureSettings";

export const TemperatureSettings: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const queryClient = useQueryClient();

  const { building, permissions } = useBuildingContext(NAME);
  const { room } = useRoomContext(NAME);

  const { mutateAsync: updateRoom } = useMutation({
    mutationFn: (data: Partial<{ comfortTemperature: number; nightTemperature: number }>) =>
      roomApi.update(building.id, [{ ids: [room.id], ...data }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
    },
  });

  const form = useAppForm({
    defaultValues: {
      comfortTemperature: room.comfortTemperature,
      nightTemperature: room.nightTemperature,
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      await updateRoom(dirty);
      formApi.reset();
    },
  });

  const { comfortTemperatureBounds, nightTemperatureBounds } = getRoomTemperatureBounds({
    preComfortTemperature: building.preComfortTemperature,
  });

  return (
    <form
      className="flex h-full flex-col justify-between"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      data-testid={NAME}
    >
      <form.AppForm>
        <div className="flex h-full flex-col gap-[20px]">
          <Field.Root className="flex flex-col gap-[10px]">
            <Field.Label className="text-primary-blue">{m.room()}</Field.Label>
            <CanAccess isAuthorized={permissions.room?.comfortTemperature?.update}>
              <form.AppField name="comfortTemperature">
                {field => (
                  <field.Slider
                    className="w-full"
                    label={`${m.comfort()} (°C)`}
                    min={TEMPERATURE_MIN}
                    max={TEMPERATURE_MAX}
                    step={TEMPERATURE_STEP}
                    valueLabelDisplay="auto"
                    valueLabelFormat={`${comfortTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) < ${field.state.value}°C (${m.comfort()})`}
                    onValueChange={nextValue => {
                      if (nextValue === undefined) return;
                      field.handleChange(clampTemperature(nextValue, comfortTemperatureBounds));
                    }}
                  />
                )}
              </form.AppField>
            </CanAccess>
            <CanAccess isAuthorized={permissions.room?.nightTemperature?.update && room.type === RoomType.ROOM}>
              <form.AppField name="nightTemperature">
                {field => (
                  <field.Slider
                    className="w-full"
                    label={`${m.night()} (°C)`}
                    defaultValue={DEFAULT_NIGHT_TEMPERATURE}
                    min={TEMPERATURE_MIN}
                    max={TEMPERATURE_MAX}
                    step={TEMPERATURE_STEP}
                    valueLabelDisplay="auto"
                    valueLabelFormat={`${nightTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) < ${field.state.value}°C (${m.comfort()})`}
                    onValueChange={nextValue => {
                      if (nextValue === undefined) return;
                      field.handleChange(clampTemperature(nextValue, nightTemperatureBounds));
                    }}
                  />
                )}
              </form.AppField>
            </CanAccess>
          </Field.Root>

          <CanAccess isAuthorized={!isMobile}>
            <Field.Root className="flex flex-col gap-[10px]">
              <Field.Label className="text-primary-blue">{m.building()}</Field.Label>
              <Alert severity="help">{m.room_temperatures_building_help()}</Alert>
              <Button asChild className="flex items-center gap-[10px] text-base text-primary-blue">
                <Link to="/buildings/$buildingId/settings/buildings/temperatures" params={{ buildingId: building.id }}>
                  {m.pre_comfort_and_absence_temperatures()}
                  <ArrowRightIcon />
                </Link>
              </Button>
              <Button asChild className="flex items-center gap-[10px] text-base text-primary-blue">
                <Link to="/buildings/$buildingId/settings/buildings/holidays" params={{ buildingId: building.id }}>
                  {m.frost_free_temperature()}
                  <ArrowRightIcon />
                </Link>
              </Button>
            </Field.Root>
          </CanAccess>

          <form.SubmitButton className="m-0 mx-auto" label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
};

TemperatureSettings.displayName = NAME;
